import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { Button, Grid, Loader, Popup, Table } from "semantic-ui-react";
import { getById } from "../../../redux/restdux/utils";
import { ErrorWrap } from "../../../helpers/error";
import { FilterButton } from "../../../components/common/filter-button";
import { ContentFilterPanel } from "../../../components/common/content-filter";
import { getUrlParams } from "../../../components/common/urlparams";
import { Category } from "./category";
import {
  cancelTransaction,
  fetchTransactions,
} from "../../../redux/modules/fundTransaction";
import {
  ContentFilterFormBody,
  transactionQueryFilterParams,
  transactionQueryKeyMap,
} from "../../billing/transactions-page-content-filter";

import { CancelTransactionForm } from "../../billing/transaction/cancel-transaction-form";
import { PaginationWithQuery } from "../../../components/common/pagination";
import { RoleRequired } from "../../auth/role-required";
import FilterDisplay from "../../../components/common/filter-display";
import { permissions } from "../../../constant/permissions";
import { t } from "../../../constant/translations";
import { DownloadLink } from "../../../helpers/download-link";
import { PageNavigationSimpleFromMeta } from "../../../components/common/page-navigation";
import { Markdown } from "../../../components/common/markdown";
import { LineBreakAnywhere } from "../../../components/base/elements";
import { Currency } from "../../common/currency";
const StyledContentFilterPanel = styled(ContentFilterPanel)`
  min-height: 650px;
  overflow-y: auto;
`;

const Inline = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  justify-content: space-between;
`;

const TransactionsPageComponent = () => {
  const [filterOpen, setFilterOpen] = useState(false);

  const location = useLocation();
  const blocks = useSelector((state) => state.blocks.entities);
  const error = useSelector((state) =>
    ErrorWrap.fromError(state.fundTransaction.error)
  );
  const loading = useSelector((state) => state.fundTransaction.pending);
  const transactions = useSelector((state) => state.fundTransaction.entities);
  const meta = useSelector((state) => state.fundTransaction.meta);
  const dispatch = useDispatch();

  const getFilters = () => {
    const argFilters = getUrlParams(location);
    if (!argFilters.from_date && !argFilters.to_date) {
      const today = dayjs();
      const lastDate = today.subtract(12, "month");
      argFilters.from_date = lastDate.format("YYYY-MM-DD");
    }
    argFilters["funds_only"] = true;
    argFilters["ordering"] = "-created_at";
    return argFilters;
  };

  const getFiltersAsQueryParams = () => {
    const filters = getFilters();
    return Object.keys(filters)
      .map((key) => `${key}=${filters[key]}`)
      .join("&");
  };

  useEffect(() => {
    dispatch(fetchTransactions(getFilters()));
  }, [location.search]);

  const renderTransactions = () => {
    if (!transactions) {
      return <Loader active />;
    }

    const handleCancelTransaction = (data) => {
      dispatch(cancelTransaction(data));
    };

    const transactionList = transactions.map((trans) => {
      const block = getById(trans.block, blocks);
      const blockName = block ? block.name : "";
      return (
        <Table.Row key={trans.id} negative={trans.cancel_at}>
          <Table.Cell>
            {dayjs(trans.created_at).format("YYYY-MM-DD HH:mm")}
          </Table.Cell>
          <Table.Cell>{dayjs(trans.date).format("YYYY-MM-DD")}</Table.Cell>
          <Table.Cell>{blockName}</Table.Cell>
          <Table.Cell>{trans.fund}</Table.Cell>
          <Table.Cell>
            <Currency amount={trans.amount} />
          </Table.Cell>
          <Table.Cell>
            <LineBreakAnywhere>
              <Markdown content={trans.description} />
            </LineBreakAnywhere>
          </Table.Cell>
          <Table.Cell>
            <Category transaction={trans} />
          </Table.Cell>
          <Table.Cell collapsing={true}>
            {!trans.cancel_at && (
              <RoleRequired
                staff={true}
                permission={permissions.billingTransactionDelete}
              >
                <CancelTransactionForm
                  entity={trans}
                  error={error}
                  loading={loading}
                  action={handleCancelTransaction}
                />
              </RoleRequired>
            )}
            {trans.cancel_at && (
              <Popup
                position={"left center"}
                trigger={
                  <Button
                    icon={"info"}
                    size={"tiny"}
                    basic={true}
                    floated={"right"}
                  />
                }
              >
                <h4>
                  {t("billing.transaction_reversed_on")} {trans.cancel_at}
                </h4>
                <p>{trans.cancel_reason}</p>
              </Popup>
            )}
          </Table.Cell>
        </Table.Row>
      );
    });

    return (
      <Table unstackable={true}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("labels.createdAt")}</Table.HeaderCell>
            <Table.HeaderCell>{t("labels.date")}</Table.HeaderCell>
            <Table.HeaderCell>{t("labels.block")}</Table.HeaderCell>
            <Table.HeaderCell>{t("labels.direction")}</Table.HeaderCell>
            <Table.HeaderCell>{t("labels.amount")}</Table.HeaderCell>
            <Table.HeaderCell>{t("labels.description")}</Table.HeaderCell>
            <Table.HeaderCell>{t("labels.category")}</Table.HeaderCell>
            <Table.HeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>{transactionList}</Table.Body>
      </Table>
    );
  };

  return (
    <Grid padded columns="equal">
      <Grid.Row>
        <Grid.Column>
          <Inline>
            <div>
              <PageNavigationSimpleFromMeta meta={meta} />
            </div>
            <FilterDisplay keyMap={transactionQueryKeyMap} />
            <div>
              <FilterButton onClick={() => setFilterOpen(!filterOpen)} />
              <DownloadLink
                size="tiny"
                href={`/api/fund-transaction/?format=csv&${getFiltersAsQueryParams()}`}
                basic={true}
                target={"_blank"}
                download={
                  t("management.transactions") +
                  `-${dayjs().format("YYYY-MM-DD-HHMM")}.csv`
                }
                icon={"download"}
              />
            </div>
          </Inline>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row style={{ padding: "0 0" }}>
        <Grid.Column>
          <StyledContentFilterPanel
            open={filterOpen}
            onApply={() => setFilterOpen(false)}
            queryParamConfig={transactionQueryFilterParams}
            FormBody={ContentFilterFormBody}
          >
            {renderTransactions()}
          </StyledContentFilterPanel>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export const TransactionsPage = TransactionsPageComponent;
